import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { usePostHog } from "posthog-js/react";
import { formatDate } from "shared-ui";

import { useBankAccountsUploadManager } from "./use-bank-accounts-upload-manager";
import { useBankStatementExtractionStatuses } from "./use-bank-statement-extraction-statuses";

type UseBankStatementUploadsProps = {
  customerDomain: string;
  requiredStatementMonths: number;
};

export const useBankStatementUploads = ({ customerDomain, requiredStatementMonths }: UseBankStatementUploadsProps) => {
  const posthog = usePostHog();
  const [hasAcceptedUploadThisSession, setHasAcceptedUploadThisSession] = useState(false);

  const statementPeriodStart = useMemo(
    () => formatDate(dayjs().subtract(requiredStatementMonths, "month").toISOString()),
    [requiredStatementMonths]
  );

  const {
    uploadsState,
    onFilesAdded: addFiles,
    onFileRemoved,
    onFileRetry,
  } = useBankAccountsUploadManager(customerDomain);
  const uploadsInProgress = uploadsState.uploadsStarted && !uploadsState.uploadsComplete;
  const confirmedUploads = uploadsState.uploads.flatMap((upload) => (upload.uploadedFile ? [upload.uploadedFile] : []));

  const onFilesAdded: typeof addFiles = (files, rejectedFiles) => {
    if (files.length > 0) {
      posthog?.capture("onboarding_bank_upload_selected", { upload_type: "bank_statement" });
      setHasAcceptedUploadThisSession(true);
    }
    addFiles(files, rejectedFiles);
  };

  const extractionStatusItems = useBankStatementExtractionStatuses(customerDomain, confirmedUploads);
  const extractedAccounts = extractionStatusItems.flatMap(({ status }) =>
    status?.extractionState === "completed"
      ? status.accounts.map((account) => ({
          objectKey: status.objectKey,
          institutionName: status.institutionName,
          businessName: status.businessName,
          account,
        }))
      : []
  );
  const pendingOrFailedExtractions = extractionStatusItems.filter(
    ({ status }) => status?.extractionState !== "completed"
  );

  return {
    uploadsState,
    onFilesAdded,
    onFileRemoved,
    onFileRetry,
    uploadsInProgress,
    hasAcceptedUploadThisSession,
    extractedAccounts,
    pendingOrFailedExtractions,
    statementPeriodStart,
  };
};
